import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import InputLabelWrap from 'components/Common/Form/Input/InputLabelWrap'
import OccupancyItem from 'components/Common/OccupancyPicker/OccupancyItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import SolidPlusIcon from 'LuxKit/Icons/solid/SolidPlusIcon'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { TravellerRoom } from 'tripPlanner/types/common'

export type TravellerRoomWithId = TravellerRoom & { id: string }

function addIdToRoom(room: TravellerRoom): TravellerRoomWithId {
  return { ...room, id: uuidV4() }
}

export function addIdsToRooms(
  rooms: Array<TravellerRoom>,
): Array<TravellerRoomWithId> {
  return rooms.map(addIdToRoom)
}

export const defaultRoom: TravellerRoom = {
  adults: 2,
  childAges: [],
}

function travellersToOccupants(travellers: TravellerRoom): App.Occupants {
  const { adults, childAges } = travellers
  return {
    adults,
    children: childAges.length,
    infants: 0,
    childrenAge: childAges,
  }
}

function occupantsToTravellers(occupants: App.Occupants): TravellerRoom {
  const { adults, childrenAge } = occupants
  return {
    adults,
    childAges: childrenAge ?? [],
  }
}

const MAX_ROOMS = 10

interface Props {
  travellerRooms: Array<TravellerRoomWithId>
  onChange: (travellerRooms: Array<TravellerRoomWithId>) => void
  allowEmpty: boolean
}

function TripTravellersField({ travellerRooms, onChange, allowEmpty }: Props) {
  const intl = useIntl()
  const updateRoom = useCallback(
    (index: number, occupancy: App.Occupants) => {
      const roomId = travellerRooms[index].id
      const newTravellerRooms = [...travellerRooms]
      newTravellerRooms[index] = {
        id: roomId,
        ...occupantsToTravellers(occupancy),
      }
      onChange(newTravellerRooms)
    },
    [onChange, travellerRooms],
  )

  const removeRoom = useCallback(
    (index: number) => {
      const newTravellerRooms = [...travellerRooms]
      newTravellerRooms.splice(index, 1)
      onChange(newTravellerRooms)
    },
    [onChange, travellerRooms],
  )

  const addRoom = useCallback(() => {
    const newTravellerRooms = [...travellerRooms]
    newTravellerRooms.push(addIdToRoom(defaultRoom))
    onChange(newTravellerRooms)
  }, [onChange, travellerRooms])

  const whoIsGoingLabel = intl.formatMessage({
    id: 'ElrbwEATWWmmgq_gQX67',
    defaultMessage: 'Who is going?',
    description: 'Label for the travellers selection field asking who is going on the trip or booking',
  })

  const childrenAgeLabel = intl.formatMessage({
    id: 'lGh5RpWVkWNGHwdWqi20',
    defaultMessage: 'at time of travel',
    description: 'Supplementary label indicating the age should be at the time of travel for children',
  })

  const addTravellersText = intl.formatMessage({
    id: 'PHlnLsC-WOQkfsmwdT6_',
    defaultMessage: 'Add travellers',
    description: 'Button text to add additional travellers to the current booking or trip plan',
  })

  const addAnotherRoomText = intl.formatMessage({
    id: 'XZuViasAzk8i98TYONVY',
    defaultMessage: 'Add another room',
    description: 'Button text to add another room on the hotel or room booking page',
  })

  return (
    <VerticalSpacer gap={4}>
      <InputLabelWrap label={whoIsGoingLabel} />
      <VerticalSpacer gap={20}>
        {travellerRooms.map((travellerRoom, index) => {
          const roomTitle = intl.formatMessage(
            {
              id: 'FDLyQTGCHFNcJENYqPNA',
              defaultMessage: 'Room {roomNumber}',
              description: 'Title displaying the room number for a specific room in the booking configuration',
            },
            { roomNumber: index + 1 },
          )

          return (
            <OccupancyItem
              key={travellerRoom.id}
              itemId={index}
              occupancy={travellersToOccupants(travellerRoom)}
              onChange={updateRoom}
              childrenAgeLabel={childrenAgeLabel}
              name="travellers"
              title={roomTitle}
              showRemove={allowEmpty || index > 0}
              onRemove={removeRoom}
              showResetWhenClean
            />
          )
        })}
        <div>
          {travellerRooms.length < MAX_ROOMS && (
            <TextButton
              kind="tertiary"
              horizontalOutdent="start"
              startIcon={<SolidPlusIcon />}
              onClick={addRoom}
            >
              {travellerRooms.length === 0 ? addTravellersText : addAnotherRoomText}
            </TextButton>
          )}
        </div>
      </VerticalSpacer>
    </VerticalSpacer>
  )
}

export default TripTravellersField
